/**
 * Auto-generated service - DO NOT EDIT MANUALLY
 * Generated from: auth.ts
 * Run: pnpm gen
 */

import { API_METHODS } from '../constants/api-methods';
import { getApiPath } from '../path';
import { type RequestOptions, type Transport } from '../transport';

import type {
  BeginMFAEnrollmentRequest,
  BeginMFAEnrollmentResponse,
  CheckPasswordStrengthRequest,
  CheckPasswordStrengthResponse,
  CompleteMFAEnrollmentRequest,
  CompleteMFAEnrollmentResponse,
  CompleteSSOLoginRequest,
  CompleteSSOLoginResponse,
  ConfigureSSORequest,
  ConfigureSSOResponse,
  CreateSSOConnectionRequest,
  CreateSSOConnectionResponse,
  DeleteSSOConnectionRequest,
  DeleteSSOConnectionResponse,
  DisableMFARequest,
  DisableMFAResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  GetMFAStatusRequest,
  GetMFAStatusResponse,
  GetSSOConfigRequest,
  GetSSOConfigResponse,
  GetSSOConnectionByDomainRequest,
  GetSSOConnectionByDomainResponse,
  GetSSOConnectionByOrganizationRequest,
  GetSSOConnectionByOrganizationResponse,
  GetSSOConnectionRequest,
  GetSSOConnectionResponse,
  ListSSOConnectionsRequest,
  ListSSOConnectionsResponse,
  ListSessionsRequest,
  ListSessionsResponse,
  LoginRequest,
  LoginResponse,
  LoginWithServiceAccountRequest,
  LoginWithServiceAccountResponse,
  LogoutRequest,
  LogoutResponse,
  RefreshTokenRequest,
  RefreshTokenResponse,
  RegenerateMFARecoveryCodesRequest,
  RegenerateMFARecoveryCodesResponse,
  RegisterUserRequest,
  RegisterUserResponse,
  ResetLoginFailuresRequest,
  ResetLoginFailuresResponse,
  ResetPasswordWithSignatureRequest,
  ResetPasswordWithSignatureResponse,
  RevokeSessionRequest,
  RevokeSessionResponse,
  StartSSOLoginByConnectionRequest,
  StartSSOLoginByConnectionResponse,
  StartSSOLoginRequest,
  StartSSOLoginResponse,
  UpdatePasswordRequest,
  UpdatePasswordResponse,
  UpdateSSOConnectionRequest,
  UpdateSSOConnectionResponse,
  VerifyEmailRequest,
  VerifyEmailResponse,
  VerifyMFARequest,
  VerifyMFAResponse,
} from '../types/auth';

export const createAuthService = (transport: Transport) => ({
  async login(request: LoginRequest, options: RequestOptions = {}): Promise<LoginResponse> {
    const url = getApiPath(API_METHODS.AuthService.Login);
    return transport.post<LoginResponse>(url, request, options);
  },

  async refreshToken(
    request: RefreshTokenRequest,
    options: RequestOptions = {},
  ): Promise<RefreshTokenResponse> {
    const url = getApiPath(API_METHODS.AuthService.RefreshToken);
    return transport.post<RefreshTokenResponse>(url, request, options);
  },

  async updatePassword(
    request: UpdatePasswordRequest,
    options: RequestOptions = {},
  ): Promise<UpdatePasswordResponse> {
    const url = getApiPath(API_METHODS.AuthService.UpdatePassword);
    return transport.post<UpdatePasswordResponse>(url, request, options);
  },

  async logout(_request: LogoutRequest, options: RequestOptions = {}): Promise<LogoutResponse> {
    const url = getApiPath(API_METHODS.AuthService.Logout);
    return transport.post<LogoutResponse>(url, {}, options);
  },

  async registerUser(
    request: RegisterUserRequest,
    options: RequestOptions = {},
  ): Promise<RegisterUserResponse> {
    const url = getApiPath(API_METHODS.AuthService.RegisterUser);
    return transport.post<RegisterUserResponse>(url, request, options);
  },

  async forgotPassword(
    request: ForgotPasswordRequest,
    options: RequestOptions = {},
  ): Promise<ForgotPasswordResponse> {
    const url = getApiPath(API_METHODS.AuthService.ForgotPassword);
    return transport.post<ForgotPasswordResponse>(url, request, options);
  },

  async checkPasswordStrength(
    request: CheckPasswordStrengthRequest,
    options: RequestOptions = {},
  ): Promise<CheckPasswordStrengthResponse> {
    const url = getApiPath(API_METHODS.AuthService.CheckPasswordStrength);
    return transport.post<CheckPasswordStrengthResponse>(url, request, options);
  },

  async loginWithServiceAccount(
    request: LoginWithServiceAccountRequest,
    options: RequestOptions = {},
  ): Promise<LoginWithServiceAccountResponse> {
    const url = getApiPath(API_METHODS.AuthService.LoginWithServiceAccount);
    return transport.post<LoginWithServiceAccountResponse>(url, request, options);
  },

  async resetLoginFailures(
    request: ResetLoginFailuresRequest,
    options: RequestOptions = {},
  ): Promise<ResetLoginFailuresResponse> {
    const url = getApiPath(API_METHODS.AuthService.ResetLoginFailures);
    return transport.post<ResetLoginFailuresResponse>(url, request, options);
  },

  async resetPasswordWithSignature(
    request: ResetPasswordWithSignatureRequest,
    options: RequestOptions = {},
  ): Promise<ResetPasswordWithSignatureResponse> {
    const url = getApiPath(API_METHODS.AuthService.ResetPasswordWithSignature);
    return transport.post<ResetPasswordWithSignatureResponse>(url, request, options);
  },

  async verifyEmail(
    request: VerifyEmailRequest,
    options: RequestOptions = {},
  ): Promise<VerifyEmailResponse> {
    const url = getApiPath(API_METHODS.AuthService.VerifyEmail);
    return transport.post<VerifyEmailResponse>(url, request, options);
  },

  async verifyMfa(
    request: VerifyMFARequest,
    options: RequestOptions = {},
  ): Promise<VerifyMFAResponse> {
    const url = getApiPath(API_METHODS.AuthService.VerifyMFA);
    return transport.post<VerifyMFAResponse>(url, request, options);
  },

  async beginMfaEnrollment(
    request: BeginMFAEnrollmentRequest,
    options: RequestOptions = {},
  ): Promise<BeginMFAEnrollmentResponse> {
    const url = getApiPath(API_METHODS.AuthService.BeginMFAEnrollment);
    return transport.post<BeginMFAEnrollmentResponse>(url, request, options);
  },

  async completeMfaEnrollment(
    request: CompleteMFAEnrollmentRequest,
    options: RequestOptions = {},
  ): Promise<CompleteMFAEnrollmentResponse> {
    const url = getApiPath(API_METHODS.AuthService.CompleteMFAEnrollment);
    return transport.post<CompleteMFAEnrollmentResponse>(url, request, options);
  },

  async getMfaStatus(
    _request: GetMFAStatusRequest,
    options: RequestOptions = {},
  ): Promise<GetMFAStatusResponse> {
    const url = getApiPath(API_METHODS.AuthService.GetMFAStatus);
    return transport.get<GetMFAStatusResponse>(url, options);
  },

  async disableMfa(
    _request: DisableMFARequest,
    options: RequestOptions = {},
  ): Promise<DisableMFAResponse> {
    const url = getApiPath(API_METHODS.AuthService.DisableMFA);
    return transport.post<DisableMFAResponse>(url, {}, options);
  },

  async regenerateMfaRecoveryCodes(
    _request: RegenerateMFARecoveryCodesRequest,
    options: RequestOptions = {},
  ): Promise<RegenerateMFARecoveryCodesResponse> {
    const url = getApiPath(API_METHODS.AuthService.RegenerateMFARecoveryCodes);
    return transport.post<RegenerateMFARecoveryCodesResponse>(url, {}, options);
  },

  async startSsoLogin(
    request: StartSSOLoginRequest,
    options: RequestOptions = {},
  ): Promise<StartSSOLoginResponse> {
    const { organization_id } = request;
    const url = getApiPath(API_METHODS.AuthService.StartSSOLogin, { organization_id });
    return transport.get<StartSSOLoginResponse>(url, options);
  },

  async startSsoLoginByConnection(
    request: StartSSOLoginByConnectionRequest,
    options: RequestOptions = {},
  ): Promise<StartSSOLoginByConnectionResponse> {
    const { connection_id, ...params } = request;
    const url = getApiPath(API_METHODS.AuthService.StartSSOLoginByConnection, { connection_id });
    return transport.get<StartSSOLoginByConnectionResponse>(url, { params, ...options });
  },

  async completeSsoLogin(
    request: CompleteSSOLoginRequest,
    options: RequestOptions = {},
  ): Promise<CompleteSSOLoginResponse> {
    const { connection_id, ...body } = request;
    const url = getApiPath(API_METHODS.AuthService.CompleteSSOLogin, { connection_id });
    return transport.post<CompleteSSOLoginResponse>(url, body, options);
  },

  async getSsoConfig(
    request: GetSSOConfigRequest,
    options: RequestOptions = {},
  ): Promise<GetSSOConfigResponse> {
    const { organization_id } = request;
    const url = getApiPath(API_METHODS.AuthService.GetSSOConfig, { organization_id });
    return transport.get<GetSSOConfigResponse>(url, options);
  },

  async createSsoConnection(
    request: CreateSSOConnectionRequest,
    options: RequestOptions = {},
  ): Promise<CreateSSOConnectionResponse> {
    const { organization_id, ...body } = request;
    const url = getApiPath(API_METHODS.AuthService.CreateSSOConnection, { organization_id });
    return transport.post<CreateSSOConnectionResponse>(url, body, options);
  },

  async updateSsoConnection(
    request: UpdateSSOConnectionRequest,
    options: RequestOptions = {},
  ): Promise<UpdateSSOConnectionResponse> {
    const { organization_id, connection_id, ...body } = request;
    const url = getApiPath(API_METHODS.AuthService.UpdateSSOConnection, {
      organization_id,
      connection_id,
    });
    return transport.post<UpdateSSOConnectionResponse>(url, body, options);
  },

  async listSsoConnections(
    request: ListSSOConnectionsRequest,
    options: RequestOptions = {},
  ): Promise<ListSSOConnectionsResponse> {
    const { organization_id } = request;
    const url = getApiPath(API_METHODS.AuthService.ListSSOConnections, { organization_id });
    return transport.get<ListSSOConnectionsResponse>(url, options);
  },

  async getSsoConnection(
    request: GetSSOConnectionRequest,
    options: RequestOptions = {},
  ): Promise<GetSSOConnectionResponse> {
    const { organization_id, connection_id } = request;
    const url = getApiPath(API_METHODS.AuthService.GetSSOConnection, {
      organization_id,
      connection_id,
    });
    return transport.get<GetSSOConnectionResponse>(url, options);
  },

  async deleteSsoConnection(
    request: DeleteSSOConnectionRequest,
    options: RequestOptions = {},
  ): Promise<DeleteSSOConnectionResponse> {
    const { organization_id, connection_id } = request;
    const url = getApiPath(API_METHODS.AuthService.DeleteSSOConnection, {
      organization_id,
      connection_id,
    });
    return transport.delete<DeleteSSOConnectionResponse>(url, options);
  },

  async getSsoConnectionByDomain(
    request: GetSSOConnectionByDomainRequest,
    options: RequestOptions = {},
  ): Promise<GetSSOConnectionByDomainResponse> {
    const url = getApiPath(API_METHODS.AuthService.GetSSOConnectionByDomain);
    return transport.get<GetSSOConnectionByDomainResponse>(url, { params: request, ...options });
  },

  async getSsoConnectionByOrganization(
    request: GetSSOConnectionByOrganizationRequest,
    options: RequestOptions = {},
  ): Promise<GetSSOConnectionByOrganizationResponse> {
    const url = getApiPath(API_METHODS.AuthService.GetSSOConnectionByOrganization);
    return transport.get<GetSSOConnectionByOrganizationResponse>(url, {
      params: request,
      ...options,
    });
  },

  async configureSso(
    request: ConfigureSSORequest,
    options: RequestOptions = {},
  ): Promise<ConfigureSSOResponse> {
    const { organization_id, ...body } = request;
    const url = getApiPath(API_METHODS.AuthService.ConfigureSSO, { organization_id });
    return transport.post<ConfigureSSOResponse>(url, body, options);
  },

  async listSessions(
    request: ListSessionsRequest,
    options: RequestOptions = {},
  ): Promise<ListSessionsResponse> {
    const url = getApiPath(API_METHODS.AuthService.ListSessions);
    return transport.get<ListSessionsResponse>(url, { params: request, ...options });
  },

  async revokeSession(
    request: RevokeSessionRequest,
    options: RequestOptions = {},
  ): Promise<RevokeSessionResponse> {
    const { session_id } = request;
    const url = getApiPath(API_METHODS.AuthService.RevokeSession, { session_id });
    return transport.post<RevokeSessionResponse>(url, {}, options);
  },
});

export type AuthService = ReturnType<typeof createAuthService>;
